import React from 'react';
import { useDrag } from 'react-dnd';
import {
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  AtSymbolIcon,
  PhoneIcon,
  HashtagIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  CircleStackIcon,
  CheckIcon,
  CloudArrowUpIcon,
  StarIcon,
  AdjustmentsHorizontalIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const FIELD_TYPES = [
  {
    type: 'text',
    label: 'Text Input',
    icon: DocumentTextIcon,
    description: 'Single line text input'
  },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: ChatBubbleBottomCenterTextIcon,
    description: 'Multi-line text input'
  },
  {
    type: 'email',
    label: 'Email',
    icon: AtSymbolIcon,
    description: 'Email address input'
  },
  {
    type: 'phone',
    label: 'Phone',
    icon: PhoneIcon,
    description: 'Phone number input'
  },
  {
    type: 'number',
    label: 'Number',
    icon: HashtagIcon,
    description: 'Numeric input'
  },
  {
    type: 'date',
    label: 'Date',
    icon: CalendarDaysIcon,
    description: 'Date picker'
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: ChevronDownIcon,
    description: 'Select from options'
  },
  {
    type: 'radio',
    label: 'Radio Buttons',
    icon: CircleStackIcon,
    description: 'Single choice selection'
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    icon: CheckIcon,
    description: 'Multiple choice selection'
  },
  {
    type: 'file',
    label: 'File Upload',
    icon: CloudArrowUpIcon,
    description: 'File attachment'
  },
  {
    type: 'rating',
    label: 'Rating',
    icon: StarIcon,
    description: 'Star rating input'
  },
  {
    type: 'slider',
    label: 'Slider',
    icon: AdjustmentsHorizontalIcon,
    description: 'Range slider input'
  },
  {
    type: 'signature',
    label: 'Signature',
    icon: PencilIcon,
    description: 'Digital signature pad'
  }
];

const DraggableFieldType = ({ fieldType, onAddField, disabled }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'fieldType',
    item: { fieldType: fieldType.type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [fieldType.type]);

  const Icon = fieldType.icon;

  const handleClick = () => {
    if (!disabled && onAddField) {
      onAddField(fieldType.type);
    }
  };

  return (
    <div
      ref={drag}
      onClick={handleClick}
      className={`
        group relative p-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 
        cursor-pointer hover:border-purple-400 dark:hover:border-purple-500 
        hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200
        ${isDragging ? 'opacity-50' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      title={fieldType.description}
    >
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Icon className="h-5 w-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
            {fieldType.label}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {fieldType.description}
          </p>
        </div>
      </div>

      {/* Drag indicator */}
      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex space-x-0.5">
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

const FieldPalette = ({ onAddField, disabled = false }) => {
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
        Basic Fields
      </div>
      
      {FIELD_TYPES.slice(0, 6).map((fieldType) => (
        <DraggableFieldType
          key={fieldType.type}
          fieldType={fieldType}
          onAddField={onAddField}
          disabled={disabled}
        />
      ))}

      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 mt-6">
        Advanced Fields
      </div>
      
      {FIELD_TYPES.slice(6).map((fieldType) => (
        <DraggableFieldType
          key={fieldType.type}
          fieldType={fieldType}
          onAddField={onAddField}
          disabled={disabled}
        />
      ))}

      {!disabled && (
        <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
                How to use
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Click or drag field types to the form canvas to add them. Configure field properties in the right panel.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldPalette;